import { type FC, type RefObject, useMemo } from "react";
import {
  type UseFormRegister,
  type UseFormWatch,
  type UseFormSetValue,
  type FieldErrors,
} from "react-hook-form";
import { SectionTitle } from "@userFrontend/components/SectionTitle";
import { Label } from "@root/shared/components/Label";
import { Checkbox } from "@root/shared/components/Checkbox";
import { TextArea } from "@root/shared/components/TextArea";
import { ErrorInformationText } from "@userFrontend/components/ErrorInformationText";
import { countCharacters } from "@root/shared/utils/countCharacters";
import { type SelectOptions } from "./type";
import { OTHER_DETAIL_KEY, OTHER_DETAIL_MAX_LENGTH } from "./schema";
import { type ViolationReportInputsValues } from "../../schema";
import * as styles from "./styles.css";

type Props = {
  // 違反行為の種類選択肢
  questions: {
    violationTypes: SelectOptions;
  };
  // React Hook Form の register 関数
  register: UseFormRegister<ViolationReportInputsValues>;
  // React Hook Form の watch 関数
  watch: UseFormWatch<ViolationReportInputsValues>;
  // React Hook Form の setValue 関数
  setValue: UseFormSetValue<ViolationReportInputsValues>;
  // フォームのエラー情報
  errors: FieldErrors<ViolationReportInputsValues>;
  // チェックボックス領域への ref（エラー時のスクロール用）
  checkboxRef?: RefObject<HTMLDivElement>;
  // 「その他」詳細入力領域への ref（エラー時のスクロール用）
  otherDetailRef?: RefObject<HTMLDivElement>;
};

// 「その他」を判定するためのラベル文字列（optionText に含まれるかで探す）
const OTHER_LABEL = "その他";

/**
 * 選択肢配列から「その他」に該当する optionId を探す
 * @param options - 違反行為の種類選択肢配列
 * @returns 「その他」の optionId、見つからない場合は undefined
 */
const findOtherOptionId = (options?: SelectOptions): number | undefined =>
  options?.find((q) => q.optionText.includes(OTHER_LABEL))?.optionId;

/**
 * 「その他」詳細入力が有効かどうかを判定
 * @param params.values - 選択中の違反行為 ID 配列
 * @param params.otherOptionId - 「その他」の optionId
 * @returns 「その他」が選択されている場合 true
 */
const resolveIsOtherEnabled = (params: { values: number[]; otherOptionId?: number }): boolean => {
  const { values, otherOptionId } = params;
  if (otherOptionId === undefined) return false;
  return values.includes(otherOptionId);
};

/**
 * 違反行為の種類選択コンポーネント
 * - 複数の違反行為をチェックボックスで選択可能
 * - 「その他」選択時は詳細入力欄が有効化される
 */
export const ViolationType: FC<Props> = ({
  questions,
  register,
  watch,
  setValue,
  errors,
  checkboxRef,
  otherDetailRef,
}) => {
  // 選択中の違反行為 ID 配列を取得
  const violationType = watch("violationType");
  const values = useMemo(() => violationType || [], [violationType]);

  // 「その他」詳細入力値を取得
  const otherDetailValue = watch(OTHER_DETAIL_KEY) || "";

  // 配列フィールド（violationType）のエラーメッセージを取得
  // React Hook Formの配列フィールドは errors.violationType.root.message に格納される場合と
  // errors.violationType.message に直接格納される場合がある
  // 参考: https://github.com/react-hook-form/react-hook-form/issues/11883
  const violationTypeErrorMessage = (() => {
    if (!errors.violationType) return undefined;

    // errors.violationType.message に直接格納されている場合
    if ("message" in errors.violationType && typeof errors.violationType.message === "string") {
      return errors.violationType.message;
    }

    // errors.violationType.root.message に格納されている場合
    if (
      "root" in errors.violationType &&
      errors.violationType.root &&
      typeof errors.violationType.root === "object" &&
      "message" in errors.violationType.root &&
      typeof errors.violationType.root.message === "string"
    ) {
      return errors.violationType.root.message;
    }

    return undefined;
  })();

  // 「その他」の optionId を取得（選択肢が変更された時のみ再計算）
  const otherOptionId = useMemo(
    () => findOtherOptionId(questions?.violationTypes),
    [questions?.violationTypes],
  );

  // 「その他」が選択されているかを判定
  const isOtherEnabled = useMemo(
    () => resolveIsOtherEnabled({ values, otherOptionId }),
    [values, otherOptionId],
  );

  const otherDetailCount = useMemo(() => {
    return countCharacters(otherDetailValue);
  }, [otherDetailValue]);

  // 「その他」詳細入力が文字数制限を超えているか
  const isOtherDetailOverLimit = isOtherEnabled && otherDetailCount > OTHER_DETAIL_MAX_LENGTH;

  /**
   * チェックボックスの変更ハンドラ
   * @param optionId - 変更された選択肢の ID
   */
  const handleCheckboxChange = (optionId: number) => {
    const currentValues = values;
    const newValues = currentValues.includes(optionId)
      ? currentValues.filter((id: number) => id !== optionId)
      : [...currentValues, optionId];
    setValue("violationType", newValues, { shouldValidate: true });
  };

  return (
    <div className={styles.outer}>
      <SectionTitle text="違反行為の疑いがある行為" />
      <div className={styles.inner}>
        <div className={styles.section} ref={checkboxRef}>
          <p className={styles.textWithLabel}>
            違反行為の疑いがある行為は何ですか？
            <Label theme="red">必須</Label>
          </p>
          {violationTypeErrorMessage && <ErrorInformationText text={violationTypeErrorMessage} />}
          <div className={styles.checkboxesOuter}>
            {questions?.violationTypes?.map(({ optionId, optionText }) => {
              const checked = values.includes(optionId);
              return (
                <label key={optionId} className={styles.label} htmlFor={`${optionId}`}>
                  <Checkbox
                    name="violationType"
                    checked={checked}
                    id={`${optionId}`}
                    onChange={() => handleCheckboxChange(optionId)}
                    hasError={!!violationTypeErrorMessage && values.length === 0}
                  />
                  <div className={styles.checkboxText}>{optionText}</div>
                </label>
              );
            })}
          </div>
          <div className={styles.reasonDetailOuter} ref={otherDetailRef}>
            <TextArea
              className={styles.textArea}
              {...register(OTHER_DETAIL_KEY)}
              value={isOtherEnabled ? otherDetailValue : ""} // 「その他」が選択されている場合のみ値をセット
              disabled={!isOtherEnabled}
              placeholder="「その他」を選択する場合、理由を記載してください。"
              hasError={!!errors[OTHER_DETAIL_KEY] || isOtherDetailOverLimit}
            />
            <div className={styles.counterText}>
              <p>
                <span
                  role="status"
                  aria-live="polite"
                  className={isOtherDetailOverLimit ? styles.textOverLimit : ""}
                >
                  {otherDetailCount}
                </span>
                /{OTHER_DETAIL_MAX_LENGTH}文字以内
              </p>
            </div>
            {errors[OTHER_DETAIL_KEY]?.message && (
              <ErrorInformationText text={errors[OTHER_DETAIL_KEY].message} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
